import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { iniciativasAPI, authAPI, negociosAPI, hitosFacturacionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SupervisionModal from '../components/SupervisionModal';
import EnlacePropuestaModal from '../components/EnlacePropuestaModal';
import EnlacesModal from '../components/EnlacesModal';
import FechaEntregaRealModal from '../components/FechaEntregaRealModal';
import MotivoRechazoModal from '../components/MotivoRechazoModal';
import AprobadaModal from '../components/AprobadaModal';
import HitoModal from '../components/HitoModal';
import MentionTextarea, { renderMentionText } from '../components/MentionTextarea';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  // Tanto 'operaciones' como 'innovacion' usan la misma vista de ejecución
  const isOpsView = from === 'operaciones' || from === 'innovacion';
  const { user } = useAuth();
  const currentUserName = user ? (user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.username) : '';
  const [project, setProject] = useState(null);
  const [pessoas, setPessoas] = useState([]);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingComentarios, setLoadingComentarios] = useState(false);
  const [error, setError] = useState(null);
  // Estado para edición inline de comentarios
  const [editingComentarioId, setEditingComentarioId] = useState(null);
  const [editingComentarioTexto, setEditingComentarioTexto] = useState('');

  // Estados para campos editables
  const [editingField, setEditingField] = useState(null);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [showAddPersonForm, setShowAddPersonForm] = useState(false);
  const [showAddHitoForm, setShowAddHitoForm] = useState(false);
  const [enlacesModalOpen, setEnlacesModalOpen] = useState(false);
  const [enlacesLoading, setEnlacesLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  
  // Estados para el modal de supervisión
  const [supervisionModalOpen, setSupervisionModalOpen] = useState(false);
  const [pendingFieldUpdate, setPendingFieldUpdate] = useState(null);
  const [supervisionStatus, setSupervisionStatus] = useState(null);
  const [supervisionLoading, setSupervisionLoading] = useState(false);
  
  // Estados para el modal de enlace propuesta
  const [enlaceModalOpen, setEnlaceModalOpen] = useState(false);
  const [enlaceActual, setEnlaceActual] = useState('');
  const [enlaceLoading, setEnlaceLoading] = useState(false);

  // Estados para el modal de fecha de entrega real
  const [fechaEntregaRealModalOpen, setFechaEntregaRealModalOpen] = useState(false);
  const [pendingFechaEntregaReal, setPendingFechaEntregaReal] = useState(null);

  // Estados para el modal de motivo de rechazo
  const [motivoRechazoModalOpen, setMotivoRechazoModalOpen] = useState(false);
  const [pendingMotivoRechazo, setPendingMotivoRechazo] = useState(null);

  // Estados para el modal de campos requeridos al aprobar
  const [aprobadaModalOpen, setAprobadaModalOpen] = useState(false);
  const [pendingAprobada, setPendingAprobada] = useState(null);
  const [aprobadaLoading, setAprobadaLoading] = useState(false);

  // Estado pendiente del select de estado (para mostrar valor seleccionado antes de confirmar modal)
  const [estadoPendiente, setEstadoPendiente] = useState(null);

  // Estados para hitos de facturación
  const [hitosFacturacion, setHitosFacturacion] = useState([]);
  const [hitoModalOpen, setHitoModalOpen] = useState(false);
  const [hitoSeleccionado, setHitoSeleccionado] = useState(null);
  const [subnegocioOptions, setSubnegocioOptions] = useState([]);
  const [zonaGeograficaOptions, setZonaGeograficaOptions] = useState([]);
  const [paisOptions, setPaisOptions] = useState([]);

  useEffect(() => {
    negociosAPI.getAll().then(res => {
      const negocios = res.data.results || res.data || [];
      const unique = [...new Set(negocios.map(n => n.subnegocio).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      setSubnegocioOptions(unique);
    }).catch(err => console.error('Error cargando subnegocio opciones:', err));

    hitosFacturacionAPI.getChoices().then(res => {
      setZonaGeograficaOptions(res.data.zona_geografica || []);
      setPaisOptions(res.data.pais || []);
    }).catch(err => console.error('Error cargando choices:', err));
  }, []);
  const [nuevoHito, setNuevoHito] = useState({
    numero_hito: '',
    nombre: '',
    fecha: '',
    porcentaje: '',
    importe: '',
    cliente: '',
    sociedad_final: '',
    zona_geografica: '',
    pais: '',
    facturado: false,
    fecha_facturacion: '',
    observaciones: ''
  });

  // Opciones para el estado (basadas en el modelo Iniciativa)
  // Determinar qué opciones mostrar según si está en Desarrollo de Negocio u Operaciones
  const estadoOptionsDesarrolloNegocio = [
    { value: 'oportunidad_identificada', label: 'Identificada' },
    { value: 'en_preparacion_propuesta', label: 'En preparación' },
    { value: 'supervision', label: 'Supervisión' },
    { value: 'entregada', label: 'Entregada' },
    { value: 'stand_by', label: 'Stand-By' },
    { value: 'rechazada', label: 'Rechazada' },
    { value: 'aprobado', label: 'Aprobada' },
  ];

  const estadoOptionsOperaciones = [
    { value: 'aprobado', label: 'Aprobado' },
    { value: 'planificada', label: 'Planificada' },
    { value: 'en_curso', label: 'En curso' },
    { value: 'bloqueada', label: 'Bloqueada' },
    { value: 'cancelada', label: 'Cancelada' },
    { value: 'finalizada', label: 'Finalizada' },
  ];

  // Determinar qué opciones usar basándose en el estado actual del proyecto
  const getEstadoOptions = () => {
    if (!project) return estadoOptionsDesarrolloNegocio;
    
    // Si el estado actual es de operaciones, mostrar opciones de operaciones
    const estadosOperaciones = ['aprobado', 'planificada', 'en_curso', 'bloqueada', 'cancelada', 'finalizada'];
    if (estadosOperaciones.includes(project.estado)) {
      return estadoOptionsOperaciones;
    }
    
    // De lo contrario, mostrar opciones de desarrollo de negocio
    return estadoOptionsDesarrolloNegocio;
  };

  // Función para determinar si estamos en fase de Desarrollo de Negocio
  // basado en desde dónde navegó el usuario
  const isDesarrolloNegocio = () => {
    // Si viene desde operaciones o innovacion, mostrar vista de ejecución
    if (isOpsView) {
      return false;
    }
    // Si viene desde hitos_facturacion, desarrollo_negocio o sin parámetro, mostrar vista de desarrollo de negocio
    return true;
  };

  const estadoOptions = getEstadoOptions();

  const rolesOptions = [
    { value: 'responsable_comercial', label: 'Responsable DN' },
    { value: 'jefe_proyecto', label: 'Jefe de Proyecto' },
    { value: 'responsable_tecnico', label: 'Responsable Técnico' },
    { value: 'desarrollador', label: 'Desarrollador' },
    { value: 'analista', label: 'Analista' },
    { value: 'tester', label: 'Tester' },
    { value: 'otro', label: 'Otro' }
  ];

  // Función para manejar navegación de vuelta inteligente
  const handleGoBack = () => {
    const from = searchParams.get('from');
    const tab = searchParams.get('tab');

    if (from === 'innovacion') {
      navigate('/innovation');
    } else if (from === 'operaciones') {
      // Construir la URL de vuelta a operaciones preservando el estado
      const operacionesParams = new URLSearchParams();

      // Agregar tab si existe
      if (tab) {
        operacionesParams.set('tab', tab);
      }

      // Preservar otros parámetros de filtros si existen
      const filterParams = [
        'search', 'estados', 'areas', 'jefes', 'lineaNegocio',
        'negocio', 'negocios', 'innovacion', 'tipoInnovacion', 'años'
      ];

      filterParams.forEach(param => {
        const value = searchParams.get(param);
        if (value) {
          operacionesParams.set(param, value);
        }
      });

      // Navegar a operaciones con los parámetros preservados
      navigate(`/operaciones?${operacionesParams.toString()}`);
    } else if (from === 'hitos_facturacion') {
      navigate('/hitos-facturacion');
    } else {
      // Usar navegación por defecto para otros casos
      navigate(-1);
    }
  };

  useEffect(() => {
    loadProjectDetails();
  }, [id]);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      const [projectResponse, peopleResponse, comentariosResponse] = await Promise.all([
        iniciativasAPI.getById(id),
        iniciativasAPI.getPersonas(id),
        iniciativasAPI.getComentarios(id)
      ]);

      setProject(projectResponse.data);
      setPessoas(peopleResponse.data);
      setComentarios(comentariosResponse.data);

      if (from === 'hitos_facturacion') {
        const hitosResponse = await iniciativasAPI.getHitosFacturacion(id);
        setHitosFacturacion(hitosResponse.data);
      }
    } catch (err) {
      setError('Error al cargar los detalles del proyecto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'No definido';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true
    }).format(amount);
  };

  const formatCurrencyValue = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true
    }).format(amount);
  };

  const getEstadoBadgeColor = (estado) => {
    const colores = {
      'rechazada': '#dc3545',
      'cancelada': '#6c757d',
      'bloqueada': '#dc3545',
      'finalizada': '#28a745',
      'aprobado': '#28a745',
      'en_curso': '#0366d6',
      'planificada': '#0366d6',
      'supervision': '#17a2b8',
      'entregada': '#6f42c1',
      'stand_by': '#ffc107',
      'en_preparacion_propuesta': '#fd7e14',
      'oportunidad_identificada': '#6c757d'
    };
    return colores[estado] || '#0366d6'; // Azul por defecto
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'No definida';
    return new Date(dateTimeString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para formatear fecha para input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  // Función para actualizar campo del proyecto
  const updateProjectField = async (field, value) => {
    // Si se está intentando cambiar a 'rechazada', verificar motivo_tipificado
    if (field === 'estado' && value === 'rechazada' && !project.motivo_tipificado) {
      setPendingMotivoRechazo({ field, value });
      setMotivoRechazoModalOpen(true);
      return;
    }

    // Si se está intentando cambiar a 'supervision', 'entregada' o 'aprobado', verificar enlace
    if (field === 'estado' && (value === 'supervision' || value === 'entregada' || value === 'aprobado')) {
      try {
        const enlaceResponse = await iniciativasAPI.checkEnlacePropuesta(id);
        
        if (!enlaceResponse.data.tiene_enlace) {
          // Mostrar modal de enlace
          setPendingFieldUpdate({ field, value });
          setEnlaceActual(enlaceResponse.data.enlace_al_documento_ppt_sharepoint || '');
          setEnlaceModalOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error al verificar enlace:', err);
        setError('Error al verificar el enlace de la iniciativa');
        return;
      }
    }
    
    // Si se está intentando cambiar a 'entregada' o 'aprobado', verificar supervisión
    if (field === 'estado' && (value === 'entregada' || value === 'aprobado')) {
      try {
        const response = await iniciativasAPI.checkSupervision(id);
        
        if (!response.data.can_move_to_entregada) {
          // Mostrar modal de supervisión
          setPendingFieldUpdate({ field, value });
          setSupervisionStatus(response.data);
          setSupervisionModalOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error al verificar supervisión:', err);
        setError('Error al verificar la supervisión de la iniciativa');
        return;
      }
    }
    
    // Si no requiere supervisión/enlace o ya los tiene, proceder con la actualización
    try {
      // Convertir cadenas vacías a null para campos de fecha y numéricos
      let finalValue = value;
      
      // Lista de campos de fecha que deben convertirse a null si están vacíos
      const dateFields = [
        'fecha_de_entrega_propuesta_prevista', 'fecha_de_inicio_planificada', 
        'fecha_de_inicio_real', 'fecha_de_fin_planificada', 'fecha_de_fin_real',
        'fecha_de_entrega_propuesta_real', 'fecha_de_aprobacion_propuesta_firmada'
      ];
      
      // Lista de campos numéricos que deben convertirse a null si están vacíos
      const numericFields = [
        'valor_minimo_estimado', 'valor_maximo_estimado', 'duracion_minima_estimada',
        'duracion_maxima_estimada', 'importe_total_cliente', 'año',
        'coste_de_desarrollo_estimado', 'horas_desarrollo_estimado', 'coste_de_gastos_estimado',
        'hardware_estimado', 'opex_estimado', 'coste_de_alquiler_de_equipos_estimado',
        'riesgos_estimado', 'estructura_estimado', 'coste_de_desarrollo_real_manual',
        'coste_de_gastos_real', 'hardware_real', 'opex_real',
        'coste_de_alquiler_de_equipos_real', 'riesgos_real', 'estructura_real',
        'importe_total_estimado_manual', 'importe_total_real_manual'
      ];
      
      if ((dateFields.includes(field) || numericFields.includes(field)) && value === '') {
        finalValue = null;
      }
      
      // Si cambia a entregada, o a aprobado sin fecha entrega real, mostrar modal de fecha
      if (field === 'estado' && (finalValue === 'entregada' || (finalValue === 'aprobado' && !project.fecha_de_entrega_propuesta_real))) {
        setPendingFechaEntregaReal({ field, value: finalValue });
        setFechaEntregaRealModalOpen(true);
        return;
      }

      // Si cambia a aprobado, verificar campos de aprobación
      if (field === 'estado' && finalValue === 'aprobado') {
        const faltaImporte = !project.importe_total_cliente;
        const faltaFechaAprobacion = !project.fecha_de_aprobacion_propuesta_firmada;
        if (faltaImporte || faltaFechaAprobacion) {
          setPendingAprobada({ field, value: finalValue, fechaEntregaReal: null });
          setAprobadaModalOpen(true);
          return;
        }
      }

      const updatedProject = { ...project, [field]: finalValue };
      await iniciativasAPI.update(id, { [field]: finalValue });
      setProject(updatedProject);
      setEditingField(null);
    } catch (err) {
      console.error('Error al actualizar campo:', err);
      setError('Error al actualizar el campo');
    }
  };

  const handleSaveEnlaces = async (enlaces) => {
    try {
      // Actualizar cada enlace si ha cambiado (normalizar null/undefined a '' para comparar)
      const updates = {};
      const norm = (v) => v || '';
      if (norm(enlaces.enlace_al_documento_ppt_sharepoint) !== norm(project.enlace_al_documento_ppt_sharepoint)) {
        updates.enlace_al_documento_ppt_sharepoint = norm(enlaces.enlace_al_documento_ppt_sharepoint);
      }
      if (norm(enlaces.enlace_a_la_pagina_de_confluence) !== norm(project.enlace_a_la_pagina_de_confluence)) {
        updates.enlace_a_la_pagina_de_confluence = norm(enlaces.enlace_a_la_pagina_de_confluence);
      }
      if (norm(enlaces.enlace_propuesta_pdf_sharepoint) !== norm(project.enlace_propuesta_pdf_sharepoint)) {
        updates.enlace_propuesta_pdf_sharepoint = norm(enlaces.enlace_propuesta_pdf_sharepoint);
      }

      setEnlacesModalOpen(false);

      if (Object.keys(updates).length > 0) {
        setEnlacesLoading(true);
        try {
          await iniciativasAPI.partialUpdate(id, updates);
          const refreshed = await iniciativasAPI.getById(id);
          setProject(refreshed.data);
        } finally {
          setEnlacesLoading(false);
        }
      }
    } catch (err) {
      console.error('Error al actualizar enlaces:', err);
      setError('Error al actualizar los enlaces');
    }
  };
  
  const handleSupervisionConfirm = async (supervisionData) => {
    if (!pendingFieldUpdate) return;
    
    setSupervisionLoading(true);
    try {
      // Marcar supervisión
      await iniciativasAPI.marcarSupervision(id, supervisionData);

      // Si el destino es entregada o aprobado, mostrar modal de fecha entrega real antes de guardar
      if (pendingFieldUpdate.value === 'entregada' || pendingFieldUpdate.value === 'aprobado') {
        setPendingFechaEntregaReal({ ...pendingFieldUpdate });
        setFechaEntregaRealModalOpen(true);
        setSupervisionModalOpen(false);
        setSupervisionLoading(false);
        return;
      }

      // Actualizar el campo
      const updatedProject = { ...project, [pendingFieldUpdate.field]: pendingFieldUpdate.value };
      await iniciativasAPI.update(id, { [pendingFieldUpdate.field]: pendingFieldUpdate.value });
      setProject(updatedProject);
      setEditingField(null);
      setEstadoPendiente(null);
      
      setSupervisionModalOpen(false);
      setPendingFieldUpdate(null);
      setSupervisionStatus(null);
      
      // Recargar el proyecto para obtener los datos actualizados
      const projectResponse = await iniciativasAPI.getById(id);
      setProject(projectResponse.data);
    } catch (err) {
      console.error('Error al marcar supervisión y actualizar:', err);
      setError('Error al actualizar la supervisión y el estado');
    } finally {
      setSupervisionLoading(false);
    }
  };
  
  const handleSupervisionClose = () => {
    setSupervisionModalOpen(false);
    setPendingFieldUpdate(null);
    setSupervisionStatus(null);
    setEditingField(null);
    setEstadoPendiente(null);
  };
  
  const handleEnlaceConfirm = async (enlace) => {
    if (!pendingFieldUpdate) return;
    
    setEnlaceLoading(true);
    try {
      // Guardar enlace
      await iniciativasAPI.guardarEnlacePropuesta(id, { 
        enlace_al_documento_ppt_sharepoint: enlace 
      });
      
      setEnlaceModalOpen(false);
      
      // Si el estado destino es "entregada" o "aprobado", verificar supervisión
      if (pendingFieldUpdate.value === 'entregada' || pendingFieldUpdate.value === 'aprobado') {
        const response = await iniciativasAPI.checkSupervision(id);
        
        if (!response.data.can_move_to_entregada) {
          // Mostrar modal de supervisión
          setSupervisionStatus(response.data);
          setSupervisionModalOpen(true);
          setEnlaceLoading(false);
          return;
        }
      }
      
      // Si el destino es entregada, o aprobado sin fecha entrega real, mostrar modal de fecha
      if (pendingFieldUpdate.value === 'entregada' ||
          (pendingFieldUpdate.value === 'aprobado' && !project.fecha_de_entrega_propuesta_real)) {
        setPendingFechaEntregaReal({ ...pendingFieldUpdate });
        setFechaEntregaRealModalOpen(true);
        setEnlaceLoading(false);
        return;
      }

      // Si el destino es aprobado con fecha ya establecida, verificar campos de aprobación
      if (pendingFieldUpdate.value === 'aprobado') {
        const faltaImporte = !project.importe_total_cliente;
        const faltaFechaAprobacion = !project.fecha_de_aprobacion_propuesta_firmada;
        if (faltaImporte || faltaFechaAprobacion) {
          setPendingAprobada({ ...pendingFieldUpdate, fechaEntregaReal: null });
          setAprobadaModalOpen(true);
          setEnlaceLoading(false);
          return;
        }
      }

      // Actualizar el campo
      const updatedProject = { ...project, [pendingFieldUpdate.field]: pendingFieldUpdate.value };
      await iniciativasAPI.update(id, { [pendingFieldUpdate.field]: pendingFieldUpdate.value });
      setProject(updatedProject);
      setEditingField(null);
      setEstadoPendiente(null);
      
      setPendingFieldUpdate(null);
      setEnlaceActual('');
      
      // Recargar el proyecto
      const projectResponse = await iniciativasAPI.getById(id);
      setProject(projectResponse.data);
    } catch (err) {
      console.error('Error al guardar enlace y actualizar:', err);
      setError('Error al guardar el enlace y actualizar el estado');
    } finally {
      setEnlaceLoading(false);
    }
  };
  
  const handleEnlaceClose = () => {
    setEnlaceModalOpen(false);
    setPendingFieldUpdate(null);
    setEnlaceActual('');
    setEditingField(null);
    setEstadoPendiente(null);
  };

  // Confirmar fecha de entrega real y aplicar el cambio de estado final
  const handleFechaEntregaRealConfirm = async (fecha) => {
    setFechaEntregaRealModalOpen(false);
    try {
      // Para aprobado, verificar campos de aprobación antes de guardar
      if (pendingFechaEntregaReal.value === 'aprobado') {
        const faltaImporte = !project.importe_total_cliente;
        const faltaFechaAprobacion = !project.fecha_de_aprobacion_propuesta_firmada;
        if (faltaImporte || faltaFechaAprobacion) {
          setPendingAprobada({ ...pendingFechaEntregaReal, fechaEntregaReal: fecha });
          setAprobadaModalOpen(true);
          return;
        }
      }
      await iniciativasAPI.update(id, {
        fecha_de_entrega_propuesta_real: fecha,
        [pendingFechaEntregaReal.field]: pendingFechaEntregaReal.value
      });
      setEditingField(null);
      setEstadoPendiente(null);
      setPendingFechaEntregaReal(null);
      setPendingFieldUpdate(null);
      setSupervisionStatus(null);
      const projectResponse = await iniciativasAPI.getById(id);
      setProject(projectResponse.data);
    } catch (err) {
      console.error('Error al guardar fecha y estado:', err);
      setError('Error al guardar la fecha de entrega real y actualizar el estado');
    }
  };

  const handleFechaEntregaRealClose = () => {
    setFechaEntregaRealModalOpen(false);
    setPendingFechaEntregaReal(null);
    setEstadoPendiente(null);
  };

  const handleAprobadaConfirm = async (data) => {
    if (!pendingAprobada) return;
    try {
      setAprobadaLoading(true);
      const updateData = {
        [pendingAprobada.field]: pendingAprobada.value,
        ...data
      };
      if (pendingAprobada.fechaEntregaReal) {
        updateData.fecha_de_entrega_propuesta_real = pendingAprobada.fechaEntregaReal;
      }
      await iniciativasAPI.update(id, updateData);
      setAprobadaModalOpen(false);
      setPendingAprobada(null);
      setPendingFechaEntregaReal(null);
      setPendingFieldUpdate(null);
      setSupervisionStatus(null);
      setEditingField(null);
      setEstadoPendiente(null);
      const projectResponse = await iniciativasAPI.getById(id);
      setProject(projectResponse.data);
    } catch (err) {
      console.error('Error al aprobar iniciativa:', err);
      setError('Error al guardar los datos de aprobación');
    } finally {
      setAprobadaLoading(false);
    }
  };

  const handleAprobadaClose = () => {
    setAprobadaModalOpen(false);
    setPendingAprobada(null);
    setEditingField(null);
    setEstadoPendiente(null);
  };

  const handleMotivoRechazoConfirm = async (motivo) => {
    if (!pendingMotivoRechazo) return;
    try {
      await iniciativasAPI.update(id, {
        motivo_tipificado: motivo,
        [pendingMotivoRechazo.field]: pendingMotivoRechazo.value
      });
      setMotivoRechazoModalOpen(false);
      setPendingMotivoRechazo(null);
      setEditingField(null);
      setEstadoPendiente(null);
      const projectResponse = await iniciativasAPI.getById(id);
      setProject(projectResponse.data);
    } catch (err) {
      console.error('Error al guardar motivo de rechazo:', err);
      setError('Error al guardar el motivo de rechazo');
    }
  };

  const handleMotivoRechazoClose = () => {
    setMotivoRechazoModalOpen(false);
    setPendingMotivoRechazo(null);
    setEditingField(null);
    setEstadoPendiente(null);
  };

  // Función para actualizar persona del equipo
  const updateTeamMember = async (personaId, updates) => {
    try {
      await iniciativasAPI.updatePersonaRelacion(id, personaId, updates);
      // Recargar la lista de personas
      const response = await iniciativasAPI.getPersonas(id);
      setPessoas(response.data);
      setEditingTeamMember(null);
    } catch (err) {
      console.error('Error al actualizar miembro del equipo:', err);
      setError('Error al actualizar el miembro del equipo');
    }
  };

  // Función para eliminar persona del equipo
  const removeTeamMember = async (personaId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta persona del equipo?')) {
      return;
    }

    try {
      await iniciativasAPI.eliminarPersona(id, personaId);
      const response = await iniciativasAPI.getPersonas(id);
      setPessoas(response.data);
    } catch (err) {
      console.error('Error al eliminar miembro del equipo:', err);
      setError('Error al eliminar el miembro del equipo');
    }
  };

  // Función para cargar usuarios disponibles
  const loadAvailableUsers = async () => {
    try {
      // Usar el API correcto para obtener personas
      const response = await require('../services/api').personasAPI.getAll();
      // Soporta respuesta paginada y no paginada
      const users = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data.results) ? response.data.results : []);
      setAvailableUsers(users);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setAvailableUsers([]);
    }
  };

  // Función para agregar persona al equipo
  const addTeamMember = async (personaData) => {
    try {
      await iniciativasAPI.agregarPersona(id, personaData);
      const response = await iniciativasAPI.getPersonas(id);
      setPessoas(response.data);
      setShowAddPersonForm(false);
    } catch (err) {
      console.error('Error al agregar miembro del equipo:', err);
      setError('Error al agregar el miembro del equipo');
    }
  };

  // Componente para agregar persona al equipo
  const AddPersonForm = ({ availableUsers, rolesOptions, onSubmit, onCancel }) => {
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
    const [dedicacion, setDedicacion] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!selectedUser || !selectedRole) {
        alert('Por favor selecciona un usuario y un rol');
        return;
      }

      onSubmit({
        persona_id: selectedUser,
        rol: selectedRole,
        fecha_inicio: fechaInicio,
        porcentaje_dedicacion: dedicacion || null,
        activo: true
      });
    };

    return (
      <form onSubmit={handleSubmit} className="add-person-form-content">
        <div className="form-row">
          <div className="form-group">
            <label>Usuario:</label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
            >
              <option value="">Seleccionar usuario...</option>
              {availableUsers.filter(user =>
                !pessoas.some(p => p.persona.id === user.id)
              ).map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Rol:</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              required
            >
              <option value="">Seleccionar rol...</option>
              {rolesOptions.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Fecha de Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Dedicación (%):</label>
            <input
              type="number"
              min="0"
              max="100"
              value={dedicacion}
              onChange={(e) => setDedicacion(e.target.value)}
              placeholder="Opcional"
            />
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-form-submit">
            Agregar al Equipo
          </button>
          <button type="button" className="btn-form-cancel" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </form>
    );
  };

  const agregarComentario = async () => {
    if (!nuevoComentario.trim()) return;

    try {
      setLoadingComentarios(true);

      // Verificar y obtener CSRF token si no existe
      let csrfToken = localStorage.getItem('csrfToken');
      if (!csrfToken) {
        console.log('No hay CSRF token, obteniendo uno nuevo...');
        try {
          const csrfResponse = await authAPI.getCsrfToken();
          csrfToken = csrfResponse.data.csrfToken;
          localStorage.setItem('csrfToken', csrfToken);
          console.log('CSRF token obtenido:', csrfToken);
        } catch (csrfError) {
          console.error('Error obteniendo CSRF token:', csrfError);
        }
      }

      const response = await iniciativasAPI.agregarComentario(id, {
        contenido: nuevoComentario
      });

      setComentarios([response.data, ...comentarios]);
      setNuevoComentario('');
    } catch (err) {
      console.error('Error al agregar comentario:', err);
      if (err.response?.status === 403) {
        setError('Error de permisos. Intentando obtener nuevo token CSRF...');
        // Intentar obtener nuevo token y reintentar
        try {
          const csrfResponse = await authAPI.getCsrfToken();
          const newToken = csrfResponse.data.csrfToken;
          localStorage.setItem('csrfToken', newToken);
          console.log('Nuevo CSRF token obtenido, reintentando...');

          const response = await iniciativasAPI.agregarComentario(id, {
            contenido: nuevoComentario
          });

          setComentarios([response.data, ...comentarios]);
          setNuevoComentario('');
          setError(null);
        } catch (retryErr) {
          console.error('Error en reintento:', retryErr);
          setError('Error al agregar el comentario después del reintento');
        }
      } else {
        setError('Error al agregar el comentario');
      }
    } finally {
      setLoadingComentarios(false);
    }
  };

  const eliminarComentario = async (comentarioId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este comentario?')) {
      return;
    }

    try {
      await iniciativasAPI.eliminarComentario(id, comentarioId);
      setComentarios(comentarios.filter(c => c.id !== comentarioId));
    } catch (err) {
      console.error('Error al eliminar comentario:', err);
      setError('Error al eliminar el comentario');
    }
  };

  const guardarEdicionComentario = async (comentarioId) => {
    if (!editingComentarioTexto.trim()) return;
    try {
      const res = await iniciativasAPI.actualizarComentario(id, comentarioId, { contenido: editingComentarioTexto });
      setComentarios(comentarios.map(c => c.id === comentarioId ? res.data : c));
      setEditingComentarioId(null);
      setEditingComentarioTexto('');
    } catch (err) {
      console.error('Error al actualizar comentario:', err);
      setError('Error al actualizar el comentario');
    }
  };

  const handleAgregarHito = async () => {
    try {
      await iniciativasAPI.agregarHitoFacturacion(id, {
        ...nuevoHito,
        porcentaje: nuevoHito.porcentaje || null,
        importe: nuevoHito.importe || null
      });
      setNuevoHito({
        numero_hito: '',
        nombre: '',
        fecha: '',
        porcentaje: '',
        importe: '',
        cliente: '',
        sociedad_final: '',
        zona_geografica: '',
        pais: '',
        facturado: false,
        fecha_facturacion: '',
        observaciones: ''
      });
      const hitosResponse = await iniciativasAPI.getHitosFacturacion(id);
      setHitosFacturacion(hitosResponse.data);
      setShowAddHitoForm(false);
    } catch (err) {
      console.error('Error agregando hito:', err);
    }
  };

  const handleAbrirHitoModal = (hito = null) => {
    setHitoSeleccionado(hito ? {
      ...hito,
      iniciativa: hito.iniciativa || parseInt(id),
      sociedad_final: hito.sociedad_final || project?.negocio_estructura_detail?.nombre_sociedad_final || ''
    } : null);
    setHitoModalOpen(true);
  };

  const handleCerrarHitoModal = () => {
    setHitoModalOpen(false);
    setHitoSeleccionado(null);
  };

  const handleGuardarHitoModal = async () => {
    const hitosResponse = await iniciativasAPI.getHitosFacturacion(id);
    setHitosFacturacion(hitosResponse.data);
    handleCerrarHitoModal();
  };



  if (loading) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-content">
          <div className="loading-state">Cargando detalles del proyecto...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-content">
          <div className="error-state">{error}</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-container">
        <div className="project-detail-content">
          <div className="error-state">Proyecto no encontrado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <div className="project-detail-content fade-in">
        {/* Header */}
        <div className="project-header">
          <div className="project-header-main">
            <div className="project-header-left">
              <h1 className="project-title">{project.nombre_de_la_oportunidad}</h1>
              <p className="project-subtitle">
                {project.negocio_estructura_detail ?
                  `${project.negocio_estructura_detail.negocio} • ${project.negocio_estructura_detail.subnegocio}` :
                  'Sin información de negocio'
                }
              </p>
              {isOpsView && project.fecha_de_fin_planificada && (() => {
                const hoy = new Date();
                const fin = new Date(project.fecha_de_fin_planificada);
                const dias = Math.ceil((fin - hoy) / (1000 * 60 * 60 * 24));
                const semanas = Math.ceil(dias / 7);
                const color = dias < 0 ? '#d32f2f' : dias <= 14 ? '#e67e00' : '#57606a';
                const label = dias < 0 ? `vencido hace ${Math.ceil(Math.abs(dias) / 7)} sem.` : dias === 0 ? 'vence hoy' : `${semanas} sem. restantes`;
                return (
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>Fin planificado: <strong>{formatDate(project.fecha_de_fin_planificada)}</strong> — {label}</span>
                  </p>
                );
              })()}
            </div>
            <div className="project-header-right">
              <button
                onClick={() => navigate(`/projects/${id}/editar${from ? `?from=${from}` : ''}`)}
                className="btn-azure btn-primary"
                style={{ marginRight: '10px' }}
              >
                ✏️ Editar Iniciativa
              </button>
              <button
                onClick={handleGoBack}
                className="btn-azure btn-secondary"
                title={searchParams.get('from') === 'operaciones' ? 'Volver a Operaciones' : searchParams.get('from') === 'hitos_facturacion' ? 'Volver a Facturación' : 'Volver'}
              >
                ← Volver
              </button>
            </div>
          </div>
          <div className="project-badges">
            {/* Status */}
            {project.estado_display && (
              <span className="project-badge badge-status">
                {project.estado_display}
              </span>
            )}
            {/* Classification badges */}
            {project.innovacion && (
              <span className="project-badge badge-innovation">
                INNOVACIÓN
              </span>
            )}
            {project.en_uso_escalado && (
              <span className="project-badge badge-in-use">
                EN USO/ESCALADO
              </span>
            )}
            {project.productivizado && (
              <span className="project-badge badge-production">
                PRODUCTIVIZADO
              </span>
            )}
            {/* Año badge */}
            {project.año && (
              <span className="project-badge badge-year">
                AÑO {project.año}
              </span>
            )}
            {/* Madurez badge */}
            {project.madurez && (
              <span className={`project-badge badge-maturity ${project.madurez}`}>
                {project.madurez === 'poc' ? 'POC' :
                  project.madurez === 'piloto' ? 'PILOTO' : 'PROYECTO'}
              </span>
            )}
            {/* Padre badge */}
            {project.padre_nombre && (
              <span className="project-badge badge-parent">
                HIJO DE: <Link to={`/projects/${project.padre}`} style={{ color: 'inherit', textDecoration: 'none' }}>{project.padre_nombre}</Link>
              </span>
            )}
            {/* Hijos badge */}
            {project.hijos && project.hijos.length > 0 && (
              <span className="project-badge badge-has-children">
                TIENE {project.hijos.length} HIJOS (AGREGADO)
              </span>
            )}
            {/* Relevancia DH badge */}
            {project.relevancia_para_el_dh && (
              <span className="project-badge badge-relevance">
                RELEVANTE PARA DH
              </span>
            )}
          </div>
        </div>

        <div className="row">
          {/* Project Information */}
          <div className={isDesarrolloNegocio() || isOpsView ? "col-md-12" : "col-md-8"} style={isOpsView ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', alignItems: 'stretch' } : undefined}>
            <div className="project-section" style={isOpsView ? { gridColumn: '1', gridRow: '1' } : undefined}>
              <div className="section-header">
                <h3 className="section-title">Información básica</h3>
              </div>
              <div className="section-content">
                {project.descripcion && (
                  <div className="info-item" style={{ marginBottom: '20px' }}>
                    <span className="info-label">Descripción:</span>
                    <div 
                      className="info-value" 
                      style={{ 
                        whiteSpace: 'pre-wrap',
                        minHeight: '100px',
                        maxHeight: '400px',
                        overflow: 'auto',
                        resize: 'vertical',
                        padding: '12px',
                        border: '1px solid #e1e4e8',
                        borderRadius: '6px',
                        backgroundColor: '#f6f8fa',
                        lineHeight: '1.6'
                      }}
                    >
                      {project.descripcion}
                    </div>
                  </div>
                )}

                {isDesarrolloNegocio() ? (
                  <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                    {/* Vista para Desarrollo de Negocio - Layout compacto */}
                    {/* Estado */}
                    <div className="info-item">
                      <span className="info-label">Estado:</span>
                      <div className="info-value">
                        <select
                          className="estado-select-badge"
                          value={estadoPendiente ?? project.estado}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue !== project.estado) {
                              setEstadoPendiente(newValue);
                              updateProjectField('estado', newValue);
                            }
                          }}
                          style={{ backgroundColor: getEstadoBadgeColor(estadoPendiente ?? project.estado) }}
                        >
                          {estadoOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Subnegocio */}
                    <div className="info-item">
                      <span className="info-label">Subnegocio:</span>
                      <div className={`info-value ${!project.negocio_estructura_detail?.subnegocio ? 'empty' : ''}`}>
                        {project.negocio_estructura_detail?.subnegocio || 'No definido'}
                      </div>
                    </div>

                    {/* Contacto de Negocio */}
                    <div className="info-item">
                      <span className="info-label">Contacto de Negocio:</span>
                      <div className={`info-value ${!project.contacto_de_negocio ? 'empty' : ''}`}>
                        {project.contacto_de_negocio || 'No definido'}
                      </div>
                    </div>

                    {/* Responsable DN (Responsable Comercial) */}
                    <div className="info-item">
                      <span className="info-label">Responsable DN:</span>
                      <div className="info-value">
                        {editingField === 'responsable_dh' ? (
                          <div className="edit-field-container">
                            <select
                              className="edit-field-input"
                              defaultValue=""
                              onBlur={(e) => {
                                if (e.target.value) {
                                  // Agregar la persona seleccionada como responsable comercial
                                  const personaData = {
                                    persona_id: e.target.value,
                                    rol: 'responsable_comercial',
                                    fecha_inicio: new Date().toISOString().split('T')[0],
                                    porcentaje_dedicacion: null,
                                    activo: true
                                  };
                                  addTeamMember(personaData).then(() => {
                                    loadProjectDetails(); // Recargar para actualizar el campo
                                  });
                                }
                                setEditingField(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value) {
                                  const personaData = {
                                    persona_id: e.target.value,
                                    rol: 'responsable_comercial',
                                    fecha_inicio: new Date().toISOString().split('T')[0],
                                    porcentaje_dedicacion: null,
                                    activo: true
                                  };
                                  addTeamMember(personaData).then(() => {
                                    loadProjectDetails();
                                  });
                                  setEditingField(null);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            >
                              <option value="">Seleccionar persona...</option>
                              {availableUsers.filter(user =>
                                !pessoas.some(p => p.persona.id === user.id)
                              ).map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.full_name}
                                </option>
                              ))}
                            </select>
                            <div className="edit-field-actions">
                              <button
                                className="btn-edit-save"
                                onClick={(e) => {
                                  const select = e.target.parentElement.previousElementSibling;
                                  if (select.value) {
                                    const personaData = {
                                      persona_id: select.value,
                                      rol: 'responsable_comercial',
                                      fecha_inicio: new Date().toISOString().split('T')[0],
                                      porcentaje_dedicacion: null,
                                      activo: true
                                    };
                                    addTeamMember(personaData).then(() => {
                                      loadProjectDetails();
                                    });
                                  }
                                  setEditingField(null);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="btn-edit-cancel"
                                onClick={() => setEditingField(null)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`info-value ${!project.responsable_comercial_nombre ? 'empty editable-field' : ''}`}
                            onClick={() => {
                              if (!project.responsable_comercial_nombre) {
                                loadAvailableUsers();
                                setEditingField('responsable_dh');
                              }
                            }}
                            title={!project.responsable_comercial_nombre ? "Click para añadir responsable" : ""}
                            style={!project.responsable_comercial_nombre ? { cursor: 'pointer' } : {}}
                          >
                            {project.responsable_comercial_nombre || 'No definido - Click para añadir'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Área de Conocimiento */}
                    <div className="info-item">
                      <span className="info-label">Área de Conocimiento:</span>
                      <div className={`info-value ${!project.tecnologia_nombre ? 'empty' : ''}`}>
                        {project.tecnologia_nombre || 'No asignada'}
                      </div>
                    </div>

                    {/* Fecha de Entrega Propuesta Prevista */}
                    <div className="info-item">
                      <span className="info-label">Fecha de entrega propuesta (prevista):</span>
                      <div className="info-value">
                        {editingField === 'fecha_de_entrega_propuesta_prevista' ? (
                          <div className="edit-field-container">
                            <input
                              type="date"
                              className="edit-field-input"
                              defaultValue={formatDateForInput(project.fecha_de_entrega_propuesta_prevista)}
                              onBlur={(e) => updateProjectField('fecha_de_entrega_propuesta_prevista', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateProjectField('fecha_de_entrega_propuesta_prevista', e.target.value);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                            <div className="edit-field-actions">
                              <button
                                className="btn-edit-save"
                                onClick={(e) => {
                                  const input = e.target.parentElement.previousElementSibling;
                                  updateProjectField('fecha_de_entrega_propuesta_prevista', input.value);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="btn-edit-cancel"
                                onClick={() => setEditingField(null)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`date-range-value editable-field ${!project.fecha_de_entrega_propuesta_prevista ? 'empty' : ''}`}
                            onClick={() => setEditingField('fecha_de_entrega_propuesta_prevista')}
                            title="Click para editar"
                          >
                            {formatDate(project.fecha_de_entrega_propuesta_prevista)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Fecha de Entrega Real - usando fecha_de_entrega_propuesta_real */}
                    <div className="info-item">
                      <span className="info-label">Fecha Entrega Real:</span>
                      <div className="info-value">
                        {editingField === 'fecha_de_entrega_propuesta_real' ? (
                          <div className="edit-field-container">
                            <input
                              type="date"
                              className="edit-field-input"
                              defaultValue={formatDateForInput(project.fecha_de_entrega_propuesta_real)}
                              onBlur={(e) => updateProjectField('fecha_de_entrega_propuesta_real', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateProjectField('fecha_de_entrega_propuesta_real', e.target.value);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                            <div className="edit-field-actions">
                              <button
                                className="btn-edit-save"
                                onClick={(e) => {
                                  const input = e.target.parentElement.previousElementSibling;
                                  updateProjectField('fecha_de_entrega_propuesta_real', input.value);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="btn-edit-cancel"
                                onClick={() => setEditingField(null)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`date-range-value editable-field ${!project.fecha_de_entrega_propuesta_real ? 'empty' : ''}`}
                            onClick={() => setEditingField('fecha_de_entrega_propuesta_real')}
                            title="Click para editar"
                          >
                            {formatDate(project.fecha_de_entrega_propuesta_real)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Fecha de Aprobación */}
                    <div className="info-item">
                      <span className="info-label">Fecha de aprobación/propuesta firmada:</span>
                      <div className="info-value">
                        {editingField === 'fecha_de_aprobacion_propuesta_firmada' ? (
                          <div className="edit-field-container">
                            <input
                              type="date"
                              className="edit-field-input"
                              defaultValue={formatDateForInput(project.fecha_de_aprobacion_propuesta_firmada)}
                              onBlur={(e) => updateProjectField('fecha_de_aprobacion_propuesta_firmada', e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateProjectField('fecha_de_aprobacion_propuesta_firmada', e.target.value);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                            <div className="edit-field-actions">
                              <button
                                className="btn-edit-save"
                                onClick={(e) => {
                                  const input = e.target.parentElement.previousElementSibling;
                                  updateProjectField('fecha_de_aprobacion_propuesta_firmada', input.value);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="btn-edit-cancel"
                                onClick={() => setEditingField(null)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`date-range-value editable-field ${!project.fecha_de_aprobacion_propuesta_firmada ? 'empty' : ''}`}
                            onClick={() => setEditingField('fecha_de_aprobacion_propuesta_firmada')}
                            title="Click para editar"
                          >
                            {formatDate(project.fecha_de_aprobacion_propuesta_firmada)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Importe de Venta */}
                    <div className="info-item">
                      <span className="info-label">Importe de Venta:</span>
                      <div className="info-value">
                        {editingField === 'importe_total_cliente' ? (
                          <div className="edit-field-container">
                            <input
                              type="number"
                              step="0.01"
                              className="edit-field-input"
                              defaultValue={project.importe_total_cliente || ''}
                              placeholder="0.00"
                              onBlur={(e) => updateProjectField('importe_total_cliente', e.target.value ? parseFloat(e.target.value) : null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateProjectField('importe_total_cliente', e.target.value ? parseFloat(e.target.value) : null);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                            <div className="edit-field-actions">
                              <button
                                className="btn-edit-save"
                                onClick={(e) => {
                                  const input = e.target.closest('.edit-field-container').querySelector('input');
                                  updateProjectField('importe_total_cliente', input.value ? parseFloat(input.value) : null);
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="btn-edit-cancel"
                                onClick={() => setEditingField(null)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`editable-field ${!project.importe_total_cliente ? 'empty' : ''}`}
                            onClick={() => setEditingField('importe_total_cliente')}
                            title="Click para editar"
                          >
                            {project.importe_total_cliente ? formatCurrency(project.importe_total_cliente) : 'No definido - Click para añadir'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Probabilidad de Aprobación */}
                    <div className="info-item">
                      <span className="info-label">Probabilidad de Aprobación:</span>
                      <div className="info-value">
                        {project.probabilidad_de_aprobacion ? (
                          <span className={`probability-badge probability-${project.probabilidad_de_aprobacion}`}>
                            {project.probabilidad_de_aprobacion === 'alta' ? 'Alta' :
                              project.probabilidad_de_aprobacion === 'media' ? 'Media' : 'Baja'}
                          </span>
                        ) : (
                          <span className="empty">No definida</span>
                        )}
                      </div>
                    </div>

                    {project.estado === 'rechazada' && (
                      <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                        <span className="info-label">Motivo de Rechazo:</span>
                        <div className={`info-value ${!project.motivo_tipificado ? 'empty' : ''}`} style={{ 
                          padding: '8px 12px',
                          backgroundColor: '#fff3cd',
                          borderLeft: '4px solid #ffc107',
                          borderRadius: '4px'
                        }}>
                          {project.motivo_tipificado || 'No especificado'}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {/* Vista para Operaciones */}
                    <div className="info-item">
                      <span className="info-label">Estado:</span>
                      <div className="info-value">
                        <select
                          className="estado-select-badge"
                          value={estadoPendiente ?? project.estado}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue !== project.estado) {
                              setEstadoPendiente(newValue);
                              updateProjectField('estado', newValue);
                            }
                          }}
                          style={{ backgroundColor: getEstadoBadgeColor(estadoPendiente ?? project.estado) }}
                        >
                          {estadoOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Porcentaje de Avance:</span>
                      <div className="info-value">
                        {project.porcentaje_avance !== null && project.porcentaje_avance !== undefined ? (() => {
                          const pct = Math.min(Number(project.porcentaje_avance), 100);
                          const color = pct >= 100 ? '#4caf50' : pct >= 75 ? '#2196f3' : pct >= 50 ? '#ff9800' : '#f44336';
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '140px',paddingRight: '1rem' }}>
                              <span style={{ fontSize: '13px', fontWeight: '600', color }}>{pct}%</span>
                              <div style={{ height: '8px', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.1)', overflow: 'hidden', width: '100%' }}>
                                <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '4px', transition: 'width 0.3s ease' }} />
                              </div>
                            </div>
                          );
                        })() : <span className="empty">No definido</span>}
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Área de Conocimiento:</span>
                      <div className={`info-value ${!project.tecnologia_nombre ? 'empty' : ''}`}>
                        {project.tecnologia_nombre || 'No asignada'}
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Área de Ejecución:</span>
                      <div className={`info-value ${!project.area_ejecucion_nombre ? 'empty' : ''}`}>
                        {project.area_ejecucion_nombre || 'No asignada'}
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Clasificaciones:</span>
                      <div className="classification-badges">
                        {project.innovacion && (
                          <span className="classification-badge classification-innovation">
                            INNOVACIÓN
                          </span>
                        )}
                        {project.en_uso_escalado && (
                          <span className="classification-badge classification-usage">
                            EN USO/ESCALADO
                          </span>
                        )}
                        {project.productivizado && (
                          <span className="classification-badge classification-production">
                            PRODUCTIVIZADO
                          </span>
                        )}
                        {!project.innovacion && !project.en_uso_escalado && !project.productivizado && (
                          <span className="info-value empty">Sin clasificaciones especiales</span>
                        )}
                      </div>
                    </div>

                    {project.padre_nombre && (
                      <div className="info-item">
                        <span className="info-label">Iniciativa Padre:</span>
                        <div className="info-value">
                          <Link to={`/projects/${project.padre}`} className="team-link">
                            {project.padre_nombre}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Fechas de Planificación y Ejecución - ocupa todo el ancho */}
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="info-label">
                        Fechas de Planificación y Ejecución:
                      </span>
                      <div className="date-ranges">
                        <div className="date-range-item">
                          <span className="date-range-label">Inicio Planificado:</span>
                          {editingField === 'fecha_de_inicio_planificada' ? (
                            <div className="edit-field-container">
                              <input
                                type="date"
                                className="edit-field-input"
                                defaultValue={formatDateForInput(project.fecha_de_inicio_planificada)}
                                onBlur={(e) => updateProjectField('fecha_de_inicio_planificada', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateProjectField('fecha_de_inicio_planificada', e.target.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingField(null);
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-field-actions">
                                <button
                                  className="btn-edit-save"
                                  onClick={(e) => {
                                    const input = e.target.parentElement.previousElementSibling;
                                    updateProjectField('fecha_de_inicio_planificada', input.value);
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  className="btn-edit-cancel"
                                  onClick={() => setEditingField(null)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className="date-range-value editable-field"
                              onClick={() => setEditingField('fecha_de_inicio_planificada')}
                              title="Click para editar"
                            >
                              {formatDate(project.fecha_de_inicio_planificada)}
                            </span>
                          )}
                        </div>
                        <div className="date-range-item">
                          <span className="date-range-label">Inicio Real:</span>
                          {editingField === 'fecha_de_inicio_real' ? (
                            <div className="edit-field-container">
                              <input
                                type="date"
                                className="edit-field-input"
                                defaultValue={formatDateForInput(project.fecha_de_inicio_real)}
                                onBlur={(e) => updateProjectField('fecha_de_inicio_real', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateProjectField('fecha_de_inicio_real', e.target.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingField(null);
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-field-actions">
                                <button
                                  className="btn-edit-save"
                                  onClick={(e) => {
                                    const input = e.target.parentElement.previousElementSibling;
                                    updateProjectField('fecha_de_inicio_real', input.value);
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  className="btn-edit-cancel"
                                  onClick={() => setEditingField(null)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`date-range-value ${project.fecha_de_inicio_real ? 'real' : ''} editable-field`}
                              onClick={() => setEditingField('fecha_de_inicio_real')}
                              title="Click para editar"
                            >
                              {formatDate(project.fecha_de_inicio_real)}
                            </span>
                          )}
                        </div>
                        <div className="date-range-item">
                          <span className="date-range-label">Fin Planificado:</span>
                          {editingField === 'fecha_de_fin_planificada' ? (
                            <div className="edit-field-container">
                              <input
                                type="date"
                                className="edit-field-input"
                                defaultValue={formatDateForInput(project.fecha_de_fin_planificada)}
                                onBlur={(e) => updateProjectField('fecha_de_fin_planificada', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateProjectField('fecha_de_fin_planificada', e.target.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingField(null);
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-field-actions">
                                <button
                                  className="btn-edit-save"
                                  onClick={(e) => {
                                    const input = e.target.parentElement.previousElementSibling;
                                    updateProjectField('fecha_de_fin_planificada', input.value);
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  className="btn-edit-cancel"
                                  onClick={() => setEditingField(null)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className="date-range-value editable-field"
                              onClick={() => setEditingField('fecha_de_fin_planificada')}
                              title="Click para editar"
                            >
                              {formatDate(project.fecha_de_fin_planificada)}
                            </span>
                          )}
                        </div>
                        <div className="date-range-item">
                          <span className="date-range-label">Fin Real:</span>
                          {editingField === 'fecha_de_fin_real' ? (
                            <div className="edit-field-container">
                              <input
                                type="date"
                                className="edit-field-input"
                                defaultValue={formatDateForInput(project.fecha_de_fin_real)}
                                onBlur={(e) => updateProjectField('fecha_de_fin_real', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateProjectField('fecha_de_fin_real', e.target.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingField(null);
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-field-actions">
                                <button
                                  className="btn-edit-save"
                                  onClick={(e) => {
                                    const input = e.target.parentElement.previousElementSibling;
                                    updateProjectField('fecha_de_fin_real', input.value);
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  className="btn-edit-cancel"
                                  onClick={() => setEditingField(null)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`date-range-value ${project.fecha_de_fin_real ? 'real' : ''} editable-field`}
                              onClick={() => setEditingField('fecha_de_fin_real')}
                              title="Click para editar"
                            >
                              {formatDate(project.fecha_de_fin_real)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enlaces dentro de Información básica (solo Operaciones) */}
                {isOpsView && (project.enlace_al_documento_ppt_sharepoint || project.enlace_a_la_pagina_de_confluence || project.enlace_propuesta_pdf_sharepoint) && (
                  <div className="info-item" style={{ marginTop: '20px' }}>
                    <span className="info-label">Documentos y enlaces:</span>
                    <div className="resource-links" style={{ marginTop: '8px' }}>
                      {project.enlace_al_documento_ppt_sharepoint && (
                        <a
                          href={project.enlace_al_documento_ppt_sharepoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link ppt"
                        >
                          <span className="resource-link-icon">📄</span>
                          Documento PPT
                        </a>
                      )}
                      {project.enlace_a_la_pagina_de_confluence && (
                        <a
                          href={project.enlace_a_la_pagina_de_confluence}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link confluence"
                        >
                          <span className="resource-link-icon">📖</span>
                          Confluence
                        </a>
                      )}
                      {project.enlace_propuesta_pdf_sharepoint && (
                        <a
                          href={project.enlace_propuesta_pdf_sharepoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link pdf"
                        >
                          <span className="resource-link-icon">📋</span>
                          Propuesta PDF
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Information - Solo mostrar en Operaciones */}
            {!isDesarrolloNegocio() && from !== 'operaciones' &&
              (project.valor_minimo_estimado || project.valor_maximo_estimado ||
                project.importe_total_estimado || project.importe_total_real ||
                project.importe_total_cliente) && (
                <div className="project-section">
                  <div className="section-header">
                    <h3 className="section-title">Datos económicos</h3>
                  </div>
                  <div className="section-content">
                    <div className="financial-grid">
                      {(project.valor_minimo_estimado || project.valor_maximo_estimado) && (
                        <div className="financial-item">
                          <div className="financial-label">Rango de Valor Estimado</div>
                          <div className="financial-value">
                            {project.valor_minimo_estimado ? formatCurrency(project.valor_minimo_estimado) : 'No definido'} - {' '}
                            {project.valor_maximo_estimado ? formatCurrency(project.valor_maximo_estimado) : 'No definido'}
                          </div>
                        </div>
                      )}
                      {project.importe_total_estimado && (
                        <div className="financial-item">
                          <div className="financial-label">
                            Importe Total Estimado
                            {project.resultados_agregados && <span className="aggregated-tag" title="Valor agregado de sus hijos"> (Agregado)</span>}
                          </div>
                          <div className="financial-value">
                            {formatCurrency(project.resultados_agregados ? project.resultados_agregados.importe_total_estimado : project.importe_total_estimado)}
                          </div>
                        </div>
                      )}
                      {project.importe_total_cliente && (
                        <div className="financial-item">
                          <div className="financial-label">
                            Importe Total Cliente
                            {project.resultados_agregados && <span className="aggregated-tag" title="Valor agregado de sus hijos"> (Agregado)</span>}
                          </div>
                          <div className="financial-value">
                            {formatCurrency(project.resultados_agregados ? project.resultados_agregados.importe_total_cliente : project.importe_total_cliente)}
                          </div>
                        </div>
                      )}
                      {project.importe_total_real && (
                        <div className="financial-item">
                          <div className="financial-label">
                            Importe Total Real
                            {project.resultados_agregados && <span className="aggregated-tag" title="Valor agregado de sus hijos"> (Agregado)</span>}
                          </div>
                          <div className="financial-value">
                            {formatCurrency(project.resultados_agregados ? project.resultados_agregados.importe_total_real : project.importe_total_real)}
                          </div>
                        </div>
                      )}
                      {project.probabilidad_de_aprobacion && (
                        <div className="financial-item">
                          <div className="financial-label">Probabilidad de Aprobación</div>
                          <div className="financial-value">
                            <span className={`probability-badge probability-${project.probabilidad_de_aprobacion}`}>
                              {project.probabilidad_de_aprobacion === 'alta' ? 'Alta' :
                                project.probabilidad_de_aprobacion === 'media' ? 'Media' : 'Baja'}
                            </span>
                          </div>
                        </div>
                      )}
                      {(project.duracion_minima_estimada || project.duracion_maxima_estimada) && (
                        <div className="financial-item">
                          <div className="financial-label">Duración Estimada</div>
                          <div className="financial-value">
                            {project.duracion_minima_estimada || 0} - {project.duracion_maxima_estimada || 0} días
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Costes Estimados Detallados - Solo para Desarrollo de Negocio */}
            {isDesarrolloNegocio() && (
              <div className="project-section">
                <div className="section-header">
                  <h3 className="section-title">
                    Datos económicos
                  </h3>
                </div>
                <div className="section-content">
                  <div style={{ 
                    backgroundColor: '#ffffff',
                    border: '1px solid #e1e4e8',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* Conceptos de coste en columnas */}
                    <div style={{ 
                      padding: '14px 20px 12px 20px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: '8px 24px'
                    }}>
                      {/* Coste de desarrollo */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Costes de Desarrollo</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.coste_de_desarrollo_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.coste_de_desarrollo_estimado ? formatCurrency(project.coste_de_desarrollo_estimado) : '—'}
                        </span>
                      </div>

                      {/* Riesgos */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Coste de Riesgos</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.riesgos_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.riesgos_estimado ? formatCurrency(project.riesgos_estimado) : '—'}
                        </span>
                      </div>

                      {/* Estructura */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Coste de Estructura</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.estructura_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.estructura_estimado ? formatCurrency(project.estructura_estimado) : '—'}
                        </span>
                      </div>

                      {/* Gastos viajes */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Costes de Viajes y Dietas</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.coste_de_gastos_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.coste_de_gastos_estimado ? formatCurrency(project.coste_de_gastos_estimado) : '—'}
                        </span>
                      </div>

                      {/* Hardware */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Coste de HW y Licencias</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.hardware_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.hardware_estimado ? formatCurrency(project.hardware_estimado) : '—'}
                        </span>
                      </div>

                      {/* OPEX */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Costes de Mantenimiento</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.opex_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.opex_estimado ? formatCurrency(project.opex_estimado) : '—'}
                        </span>
                      </div>

                      {/* Alquiler equipos */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'1rem',
                        alignItems: 'center',
                        padding: '6px 2rem 6px 0',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Coste de Alquiler</span>
                        <span style={{ 
                          fontWeight: '500',
                          color: project.coste_de_alquiler_de_equipos_estimado ? '#24292f' : '#8c959f'
                        }}>
                          {project.coste_de_alquiler_de_equipos_estimado ? formatCurrency(project.coste_de_alquiler_de_equipos_estimado) : '—'}
                        </span>
                      </div>
                       
                       {/* Separador antes del total */}
                       <div style={{ 
                         gridColumn: '1 / -1',
                         borderTop: '1px solid #d0d7de',
                         margin: '8px 0'
                       }}></div>

                       {/* Importe Total Estimado */}
                      <div style={{ 
                        gridColumn: '1 / -1',
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'0.5rem',
                        alignItems: 'center',
                        padding: '0px 2rem 8px 0',
                        fontSize: '15px',
                        borderRadius: '4px',
                      }}>
                        <span style={{ 
                          color: '#24292f',
                          fontWeight: '500',
                          letterSpacing: '0.5px'
                        }}>
                          Importe Total Estimado
                        </span>
                        <span style={{ 
                          fontWeight: '500',
                          fontSize: '15px',
                          color: project.importe_total_estimado ? '#0969da' : '#8c959f'
                        }}>
                          {project.importe_total_estimado ? formatCurrency(project.importe_total_estimado) : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Línea separadora */}
                    <div style={{ 
                      borderTop: '2px solid #d0d7de',
                      margin: '0 20px'
                    }}></div>

                    {/* Total calculado */}
                    <div style={{ 
                      padding: '16px 20px 12px 20px',
                      backgroundColor: '#f6f8fa',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: '8px 24px',
                      alignItems: 'center'
                    }}>
                      {/* Importe de Venta - KPI Principal */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'2rem',
                        alignItems: 'center'
                      }}>
                        <span style={{ 
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#24292f'
                        }}>
                          IMPORTE DE VENTA
                        </span>
                        {editingField === 'importe_total_cliente_resumen' ? (
                          <div className="edit-field-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="number"
                              step="0.01"
                              className="edit-field-input"
                              style={{ width: '150px', fontSize: '14px' }}
                              defaultValue={project.importe_total_cliente || ''}
                              placeholder="0.00"
                              onBlur={(e) => {
                                updateProjectField('importe_total_cliente', e.target.value ? parseFloat(e.target.value) : null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateProjectField('importe_total_cliente', e.target.value ? parseFloat(e.target.value) : null);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                            <button
                              className="btn-edit-save"
                              style={{ padding: '2px 8px', fontSize: '12px' }}
                              onClick={(e) => {
                                const input = e.target.closest('.edit-field-container').querySelector('input');
                                updateProjectField('importe_total_cliente', input.value ? parseFloat(input.value) : null);
                              }}
                            >
                              ✓
                            </button>
                            <button
                              className="btn-edit-cancel"
                              style={{ padding: '2px 8px', fontSize: '12px' }}
                              onClick={() => setEditingField(null)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span 
                            style={{ 
                              fontSize: '20px',
                              fontWeight: '700',
                              color: project.importe_total_cliente ? '#0969da' : '#8c959f',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted'
                            }}
                            onClick={() => setEditingField('importe_total_cliente_resumen')}
                            title="Click para editar"
                          >
                            {formatCurrencyValue(project.importe_total_cliente)}
                          </span>
                        )}
                      </div>

                      {/* Coste asumido por DH */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        gap:'2rem',
                        alignItems: 'center',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#57606a' }}>Coste asumido por DH</span>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ 
                            fontWeight: '600',
                            color: (() => {
                              const costeAsumido = (project.importe_total_estimado || 0) - (project.importe_total_cliente || 0);
                              if (costeAsumido < 0) return '#d1242f';
                              if (costeAsumido > 0) return '#24292f';
                              return '#8c959f';
                            })()
                          }}>
                            {formatCurrencyValue((project.importe_total_estimado || 0) - (project.importe_total_cliente || 0))}
                          </span>
                          {project.importe_total_estimado && ((project.importe_total_estimado - (project.importe_total_cliente || 0)) > 0) && (
                            <span style={{ 
                              fontSize: '12px',
                              color: '#57606a',
                              fontWeight: '500'
                            }}>
                              ({(((project.importe_total_estimado - (project.importe_total_cliente || 0)) / project.importe_total_estimado) * 100).toFixed(1)}% del total)
                            </span>
                          )}
                        </div>
                      </div>
                      
                     

                      {/* Importe manual si existe */}
                      {project.importe_total_estimado_manual && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '13px'
                        }}>
                          <span style={{ color: '#57606a', fontStyle: 'italic' }}>Importe manual registrado</span>
                          <span style={{ 
                            fontWeight: '500',
                            color: '#57606a'
                          }}>
                            {formatCurrency(project.importe_total_estimado_manual)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hitos de Facturación - Solo para vista de Facturación */}
            {from === 'hitos_facturacion' && (
              <div className="project-section">
                <div className="section-header">
                  <h3 className="section-title">Hitos de Facturación</h3>
                </div>
                <div className="section-content">
                  {hitosFacturacion.length > 0 ? (
                    <div className="table-responsive">
                      <table className="team-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Nombre</th>
                            <th>Fecha</th>
                            <th>%</th>
                            <th>Importe</th>
                            <th>Cliente</th>
                            <th>Sociedad Final</th>
                            <th>Facturado</th>
                            <th>Zona Geográfica</th>
                            <th>País</th>
                            <th>Observaciones</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hitosFacturacion.map((hito) => (
                            <tr key={hito.id}>
                              <td><strong>{hito.numero_hito}</strong></td>
                              <td>{hito.nombre}</td>
                              <td>{hito.fecha || '-'}</td>
                              <td>{hito.porcentaje ? `${hito.porcentaje}%` : '-'}</td>
                              <td>{hito.importe ? `${parseFloat(hito.importe).toLocaleString()} €` : '-'}</td>
                              <td>{hito.cliente || '-'}</td>
                              <td>{hito.sociedad_final || '-'}</td>
                              <td>
                                <span
                                  className="project-badge badge-status"
                                  style={{ backgroundColor: hito.facturado ? '#2da44e' : '#8c959f', color: '#fff', fontSize: '0.75rem' }}
                                >
                                  {hito.facturado ? 'Sí' : 'No'}
                                </span>
                              </td>
                              <td>{hito.zona_geografica_display || hito.zona_geografica || '-'}</td>
                              <td>{hito.pais_display || hito.pais || '-'}</td>
                              <td>{hito.observaciones || '-'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button
                                  className="btn btn-sm btn-outline-dark"
                                  title="Editar"
                                  onClick={() => handleAbrirHitoModal(hito)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>

                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">No hay hitos de facturación configurados</div>
                  )}

                  {/* Botón / Formulario para agregar nuevo hito */}
                  <div style={{ marginTop: '20px' }}>
                    {!showAddHitoForm ? (
                      <button
                        className="btn-azure btn-primary"
                        onClick={() => {
                          setNuevoHito(prev => ({ ...prev, sociedad_final: project?.negocio_estructura_detail?.nombre_sociedad_final || '' }));
                          setShowAddHitoForm(true);
                        }}
                      >
                        + Agregar Hito
                      </button>
                    ) : (
                    <div style={{ paddingTop: '20px', borderTop: '1px solid #e1e4e8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#24292f' }}>Agregar Nuevo Hito</h5>
                      <button
                        className="btn-edit-cancel"
                        onClick={() => setShowAddHitoForm(false)}
                        title="Cancelar"
                      >
                        ✕
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Número</label>
                        <input
                          type="number"
                          className="edit-field-input"
                          value={nuevoHito.numero_hito}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, numero_hito: e.target.value })}
                          placeholder="1, 2, 3..."
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Nombre</label>
                        <input
                          type="text"
                          className="edit-field-input"
                          value={nuevoHito.nombre}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, nombre: e.target.value })}
                          placeholder="Ej: Primer entregable"
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Fecha</label>
                        <input
                          type="date"
                          className="edit-field-input"
                          value={nuevoHito.fecha}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, fecha: e.target.value })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Cliente</label>
                        <select
                          className="edit-field-input"
                          value={nuevoHito.cliente}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, cliente: e.target.value })}
                          style={{ width: '100%' }}
                        >
                          <option value="">— Sin cliente —</option>
                          {subnegocioOptions.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Porcentaje (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className="edit-field-input"
                          value={nuevoHito.porcentaje}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, porcentaje: e.target.value })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Importe (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="edit-field-input"
                          value={nuevoHito.importe}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, importe: e.target.value })}
                          style={{ width: '100%' }}
                        />
                        {project?.importe_total_cliente && (
                          <small style={{ color: '#57606a', fontSize: '11px' }}>
                            Precio de venta: {parseFloat(project.importe_total_cliente).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </small>
                        )}
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Fecha Facturación</label>
                        <input
                          type="date"
                          className="edit-field-input"
                          value={nuevoHito.fecha_facturacion}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, fecha_facturacion: e.target.value })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Facturado</label>
                        <input
                          type="checkbox"
                          checked={nuevoHito.facturado}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, facturado: e.target.checked })}
                          style={{ width: '18px', height: '18px', marginTop: '4px' }}
                        />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Sociedad Final</label>
                        <input
                          type="text"
                          className="edit-field-input"
                          value={nuevoHito.sociedad_final}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, sociedad_final: e.target.value })}
                          placeholder="Rellenado automáticamente. Puede modificarlo."
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Zona Geográfica</label>
                        <select
                          className="edit-field-input"
                          value={nuevoHito.zona_geografica}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, zona_geografica: e.target.value })}
                          style={{ width: '100%' }}
                        >
                          <option value="">— Sin zona —</option>
                          {zonaGeograficaOptions.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>País</label>
                        <select
                          className="edit-field-input"
                          value={nuevoHito.pais}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, pais: e.target.value })}
                          style={{ width: '100%' }}
                        >
                          <option value="">— Sin país —</option>
                          {paisOptions.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#57606a', marginBottom: '4px' }}>Observaciones</label>
                        <textarea
                          className="comment-textarea"
                          rows="2"
                          value={nuevoHito.observaciones}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, observaciones: e.target.value })}
                          placeholder="Observaciones adicionales..."
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-azure btn-primary"
                          onClick={handleAgregarHito}
                          disabled={!nuevoHito.nombre || !nuevoHito.numero_hito}
                        >
                          Guardar Hito
                        </button>
                        <button
                          className="btn-azure btn-secondary"
                          onClick={() => setShowAddHitoForm(false)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Enlaces y Recursos Section for Desarrollo de Negocio */}
            {isDesarrolloNegocio() && (
              <div className="project-section">
                <div className="section-header">
                  <h3 className="section-title">Enlaces y Recursos</h3>
                </div>
                <div className="section-content">
                  <div className="resource-links">
                    {enlacesLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: '#666' }}>
                        <span style={{ width: 18, height: 18, border: '2px solid #0078d4', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                        Actualizando enlaces...
                      </div>
                    ) : (
                      <>
                        {project.enlace_al_documento_ppt_sharepoint && (
                          <a
                            href={project.enlace_al_documento_ppt_sharepoint}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link ppt"
                          >
                            <span className="resource-link-icon">📄</span>
                            Documento PPT
                          </a>
                        )}

                        {project.enlace_a_la_pagina_de_confluence && (
                          <a
                            href={project.enlace_a_la_pagina_de_confluence}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link confluence"
                          >
                            <span className="resource-link-icon">📖</span>
                            Confluence
                          </a>
                        )}

                        {project.enlace_propuesta_pdf_sharepoint && (
                          <a
                            href={project.enlace_propuesta_pdf_sharepoint}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link pdf"
                          >
                            <span className="resource-link-icon">📋</span>
                            Propuesta PDF
                          </a>
                        )}

                        {!project.enlace_al_documento_ppt_sharepoint &&
                          !project.enlace_a_la_pagina_de_confluence &&
                          !project.enlace_propuesta_pdf_sharepoint && (
                            <div className="empty-state" style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              No hay enlaces disponibles
                              <button
                                onClick={() => setEnlacesModalOpen(true)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#0078d4',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  textDecoration: 'underline',
                                  padding: '0',
                                  margin: '0'
                                }}
                              >
                                Agregar enlaces
                              </button>
                            </div>
                          )}
                        
                        {/* Enlace sutil para editar si hay enlaces */}
                        {(project.enlace_al_documento_ppt_sharepoint || 
                          project.enlace_a_la_pagina_de_confluence || 
                          project.enlace_propuesta_pdf_sharepoint) && (
                          <button
                            onClick={() => setEnlacesModalOpen(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#0078d4',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              textDecoration: 'underline',
                              padding: '8px 0 0 0',
                              margin: '0',
                              display: 'block'
                            }}
                          >
                            ✏️ Editar enlaces
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Child Initiatives Section */}
            {project.hijos && project.hijos.length > 0 && (
              <div className="project-section" style={isOpsView ? { gridColumn: '1 / -1' } : undefined}>
                <div className="section-header">
                  <h3 className="section-title">Iniciativas Hijas</h3>
                  <p className="section-subtitle">
                    {project.hijos.length} iniciativa{project.hijos.length !== 1 ? 's' : ''} vinculada{project.hijos.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="section-content">
                  <div className="table-responsive">
                    <table className="team-table">
                      <thead>
                        <tr>
                          <th>Proyecto</th>
                          <th>Estado</th>
                          <th>Importe Venta</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.hijos.map(hijo => (
                          <tr key={hijo.id}>
                            <td>
                              <Link to={`/projects/${hijo.id}`} className="team-link">
                                {hijo.nombre_de_la_oportunidad}
                              </Link>
                            </td>
                            <td>
                              <span 
                                className="project-badge badge-status" 
                                style={{ 
                                  fontSize: '0.75rem',
                                  backgroundColor: getEstadoBadgeColor(hijo.estado)
                                }}
                              >
                                {hijo.estado_display || hijo.estado}
                              </span>
                            </td>
                            <td>{formatCurrencyValue(hijo.importe_total_cliente)}</td>
                            <td>
                              <Link
                                to={`/projects/${hijo.id}`}
                                className="btn-azure btn-primary"
                                style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                              >
                                Ver Detalle
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="aggregated-info-note">
                    * Los valores financieros y de horas de este proyecto son la suma de sus iniciativas hijas.
                  </div>
                </div>
              </div>
            )}

            {/* Información Económica — solo vista de ejecución */}
            {isOpsView && (
              <div className="project-section" style={{ gridColumn: '2', gridRow: '1' }}>
                <div className="section-header">
                  <h3 className="section-title">Información Económica</h3>
                </div>
                <div className="section-content">
                  {(() => {
                    const fmtPct = (v) =>
                      v !== null && v !== undefined
                        ? new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v) + '%'
                        : '—';
                    const fmtHrs = (v) =>
                      v !== null && v !== undefined && v !== 0
                        ? new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v) + ' h'
                        : '—';
                    const desvioColor = (v) =>
                      v === null || v === undefined ? '#57606a'
                        : v >= 20 ? '#d32f2f'
                        : v >= 10 ? '#ff9800'
                        : '#2e7d32';
                    const desviBg = (color) => ({
                      '#d32f2f': '#fdf2f2',
                      '#ff9800': '#fff8f0',
                      '#2e7d32': '#f0faf0',
                      '#57606a': '#f6f8fa'
                    }[color] || '#f6f8fa');

                    const costeOferta = project.coste_de_desarrollo_estimado ? Number(project.coste_de_desarrollo_estimado) : null;
                    const costeReal = project.coste_de_desarrollo_real != null ? Number(project.coste_de_desarrollo_real) : null;
                    const costeEsperado = project.coste_esperado ? Number(project.coste_esperado) : null;
                    const horasPropuesta = project.horas_desarrollo_estimado ? Number(project.horas_desarrollo_estimado) : null;
                    const horasReal = project.total_horas_imputadas != null ? Number(project.total_horas_imputadas) : null;
                    const horasEsperado = project.horas_esperado ? Number(project.horas_esperado) : null;
                    const avance = project.porcentaje_avance != null ? Number(project.porcentaje_avance) : null;
                    const avanceEsperado = project.porcentaje_avance_esperado != null ? Number(project.porcentaje_avance_esperado) : null;

                    const desvioAvance = (avanceEsperado !== null && avance !== null) ? avanceEsperado - avance : null;
                    const pctSobreOferta = (costeReal !== null && costeOferta) ? (costeReal / costeOferta) * 100 : null;
                    const desvioCoste = (costeReal !== null && costeOferta && avance !== null)
                      ? ((costeReal / costeOferta) - (avance / 100)) * 100 : null;
                    const desvioCostePrediccion = (costeReal !== null && costeOferta && avance)
                      ? (costeReal / ((avance / 100) * costeOferta) - 1) * 100 : null;
                    const desvioTiempo = project.desviacion_tiempo != null ? Number(project.desviacion_tiempo) : null;

                    const SubHeader = ({ text }) => (
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#57606a', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e1e4e8' }}>
                        {text}
                      </div>
                    );

                    const KpiCard = ({ label, value, color = '#24292f', bg = '#f6f8fa', border = '#e1e4e8', sub = null }) => (
                      <div style={{ backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '6px', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ fontSize: '10px', color: '#57606a' }}>{label}</div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color }}>{value}</div>
                        {sub && <div style={{ fontSize: '9px', color: '#8c959f' }}>{sub}</div>}
                      </div>
                    );

                    const DesvioCard = ({ label, value, sub }) => {
                      const color = desvioColor(value);
                      return (
                        <div style={{ backgroundColor: desviBg(color), border: '1px solid #e1e4e8', borderLeft: `3px solid ${color}`, borderRadius: '6px', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '10px', color: '#57606a' }}>{label}</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color }}>{value !== null ? fmtPct(value) : '—'}</div>
                          <div style={{ fontSize: '9px', color: '#8c959f' }}>{sub}</div>
                        </div>
                      );
                    };

                    return (
                      <>
                        {/* Costes */}
                        <div style={{ marginBottom: '12px' }}>
                          <SubHeader text="Costes" />
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                            <KpiCard label="Coste Oferta" value={costeOferta ? formatCurrency(costeOferta) : '—'} />
                            <KpiCard label="Coste Real (Dedicaciones)" value={costeReal ? formatCurrency(costeReal) : '—'} />
                            <KpiCard label="Coste Esperado" value={costeEsperado ? formatCurrency(costeEsperado) : '—'} sub="Según avance esperado" />
                            {pctSobreOferta !== null && (
                              <KpiCard
                                label="% s/C. Oferta"
                                value={fmtPct(pctSobreOferta)}
                                color={pctSobreOferta >= 100 ? '#d32f2f' : '#2da44e'}
                                bg={pctSobreOferta >= 100 ? '#fdf2f2' : '#f0faf0'}
                                border={pctSobreOferta >= 100 ? '#d32f2f' : '#e1e4e8'}
                                sub="Coste real / Coste oferta"
                              />
                            )}
                          </div>
                        </div>

                        {/* Horas */}
                        <div style={{ marginBottom: '12px' }}>
                          <SubHeader text="Horas" />
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                            <KpiCard label="Horas Propuesta" value={fmtHrs(horasPropuesta)} />
                            <KpiCard label="Horas Dedicadas" value={fmtHrs(horasReal)} />
                            <KpiCard label="Horas Esperadas" value={fmtHrs(horasEsperado)} sub="Según avance esperado" />
                          </div>
                        </div>

                        {/* Indicadores de Desvío */}
                        <div style={{ marginBottom: (project.riesgos_estimado || project.riesgos_real) ? '12px' : '0' }}>
                          <SubHeader text="Indicadores de Desvío" />
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
                            <DesvioCard
                              label="Desvío Planificación"
                              value={desvioAvance}
                              sub="%Avance esperado − %Avance real"
                            />
                            <DesvioCard
                              label="Desvío Coste"
                              value={desvioCoste}
                              sub="(Coste real / Coste oferta) − Avance"
                            />
                            <DesvioCard
                              label="Desvío Coste Predicción"
                              value={desvioCostePrediccion}
                              sub="Coste real / (Avance × Coste oferta) − 1"
                            />
                            <DesvioCard
                              label="Desvío Horas Esperadas"
                              value={desvioTiempo !== null ? Math.abs(desvioTiempo) : null}
                              sub="Desviación coste esperado en horas"
                            />
                          </div>
                        </div>

                        {/* Riesgos */}
                        {(project.riesgos_estimado || project.riesgos_real) && (
                          <div>
                            <SubHeader text="Riesgos" />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                              {project.riesgos_estimado && (
                                <div style={{ backgroundColor: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: '6px', padding: '8px 10px' }}>
                                  <div style={{ fontSize: '10px', color: '#57606a', marginBottom: '2px' }}>Coste Riesgos (Oferta)</div>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#24292f' }}>{formatCurrency(project.riesgos_estimado)}</div>
                                </div>
                              )}
                              {project.riesgos_real && (
                                <div style={{ backgroundColor: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: '6px', padding: '8px 10px' }}>
                                  <div style={{ fontSize: '10px', color: '#57606a', marginBottom: '2px' }}>Coste Riesgos (Real)</div>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#24292f' }}>{formatCurrency(project.riesgos_real)}</div>
                                </div>
                              )}
                              {project.riesgos_estimado && project.riesgos_real && (() => {
                                const desvRiesgo = ((Number(project.riesgos_real) - Number(project.riesgos_estimado)) / Number(project.riesgos_estimado)) * 100;
                                const color = desvRiesgo > 20 ? '#d32f2f' : desvRiesgo > 0 ? '#ff9800' : '#2e7d32';
                                return (
                                  <div style={{ backgroundColor: desviBg(color), border: '1px solid #e1e4e8', borderLeft: `3px solid ${color}`, borderRadius: '6px', padding: '8px 10px' }}>
                                    <div style={{ fontSize: '10px', color: '#57606a', marginBottom: '2px' }}>Desvío Riesgos</div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color }}>{fmtPct(desvRiesgo)}</div>
                                    <div style={{ fontSize: '9px', color: '#8c959f', marginTop: '2px' }}>Riesgos real vs. estimado</div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Team Members */}
            <div className="project-section" style={isOpsView ? { gridColumn: '1 / -1' } : undefined}>
              <div className="section-header">
                <h3 className="section-title">Equipo del Proyecto</h3>
                <div className="section-header-actions">
                  <p className="section-subtitle">
                    {pessoas.length} persona{pessoas.length !== 1 ? 's' : ''} involucrada{pessoas.length !== 1 ? 's' : ''}
                  </p>
                  <button
                    className="btn-add-person"
                    onClick={() => {
                      setShowAddPersonForm(true);
                      loadAvailableUsers();
                    }}
                  >
                    + Agregar Persona
                  </button>
                </div>
              </div>
              <div className="section-content">
                {showAddPersonForm && (
                  <div className="add-person-form">
                    <h5>Agregar Nueva Persona al Equipo</h5>
                    <AddPersonForm
                      availableUsers={availableUsers}
                      rolesOptions={rolesOptions}
                      onSubmit={addTeamMember}
                      onCancel={() => setShowAddPersonForm(false)}
                    />
                  </div>
                )}

                {pessoas.length === 0 ? (
                  <div className="empty-state">No hay personas asignadas a este proyecto.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="team-table">
                      <thead>
                        <tr>
                          <th>Persona</th>
                          <th>Rol</th>
                          <th>Última dedicación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pessoas.map(relacion => (
                          <tr key={relacion.id}>
                            <td>
                              <Link
                                to={`/people/${relacion.persona.id}`}
                                className="team-link"
                              >
                                {relacion.persona_nombre}
                              </Link>
                            </td>
                            <td>
                              {editingTeamMember === `${relacion.id}_rol` ? (
                                <div className="edit-field-container">
                                  <select
                                    className="edit-field-input"
                                    defaultValue={relacion.rol}
                                    onBlur={(e) => updateTeamMember(relacion.id, { rol: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        updateTeamMember(relacion.id, { rol: e.target.value });
                                      } else if (e.key === 'Escape') {
                                        setEditingTeamMember(null);
                                      }
                                    }}
                                    autoFocus
                                  >
                                    {rolesOptions.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="edit-field-actions">
                                    <button
                                      className="btn-edit-save"
                                      onClick={(e) => {
                                        const select = e.target.parentElement.previousElementSibling;
                                        updateTeamMember(relacion.id, { rol: select.value });
                                      }}
                                    >
                                      ✓
                                    </button>
                                    <button
                                      className="btn-edit-cancel"
                                      onClick={() => setEditingTeamMember(null)}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span
                                  className="role-badge editable-field"
                                  onClick={() => setEditingTeamMember(`${relacion.id}_rol`)}
                                  title="Click para editar rol"
                                >
                                  {relacion.rol_display}
                                </span>
                              )}
                            </td>
                            <td>
                              {relacion.ultima_dedicacion
                                ? <span style={{ fontSize: '0.85rem', color: '#24292f' }}>{formatDate(relacion.ultima_dedicacion)}</span>
                                : <span className="info-value empty" style={{ fontSize: '0.85rem' }}>Sin registros</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar with Key Info */}
          {!isDesarrolloNegocio() && from !== 'operaciones' && (
            <div className="col-md-4">
            <div className="sidebar-section">
              <div className="sidebar-header">
                <h4 className="sidebar-title">Información Clave</h4>
              </div>
              <div className="sidebar-content">
                {/* Innovation and Classification */}
                <div className="sidebar-item">
                  <span className="sidebar-label">Clasificaciones:</span>
                  <div className="classification-badges">
                    {project.innovacion && (
                      <span className="classification-badge classification-innovation">Innovación</span>
                    )}
                    {project.tipo_innovacion && (
                      <span className={`classification-badge ${project.tipo_innovacion === 'innocard' ? 'classification-innocard' : 'classification-innotrans'}`}>
                        {project.tipo_innovacion === 'innocard' ? 'Innocard' : 'Innotrans'}
                      </span>
                    )}
                    {project.en_uso_escalado && (
                      <span className="classification-badge classification-usage">En uso/Escalado</span>
                    )}
                    {project.productivizado && (
                      <span className="classification-badge classification-production">Productivizado</span>
                    )}
                    {project.relevancia_para_el_dh && (
                      <span className="classification-badge classification-relevance">Relevante para DH</span>
                    )}
                    {!project.innovacion && !project.en_uso_escalado && !project.productivizado && !project.relevancia_para_el_dh && (
                      <span className="sidebar-value" style={{ color: '#8b949e', fontStyle: 'italic' }}>Sin clasificaciones especiales</span>
                    )}
                  </div>
                </div>

                {/* Project Type and Maturity */}
                <div className="sidebar-item">
                  <span className="sidebar-label">Tipo de Iniciativa:</span>
                  <div className="sidebar-value">
                    {project.tipo_de_iniciativa ?
                      project.tipo_de_iniciativa.charAt(0).toUpperCase() + project.tipo_de_iniciativa.slice(1) :
                      <span style={{ color: '#8b949e', fontStyle: 'italic' }}>No definido</span>}
                  </div>
                </div>

                {/* Review Status */}
                {(project.revision_lt || project.revision_dn || project.propuesta_enviada) && (
                  <div className="sidebar-item">
                    <span className="sidebar-label">Estado de Revisiones:</span>
                    <div style={{ marginTop: '4px' }}>
                      {project.revision_lt && (
                        <div className="review-status">
                          <span className="review-icon review-success">✓</span>
                          Revisión LT
                        </div>
                      )}
                      {project.revision_dn && (
                        <div className="review-status">
                          <span className="review-icon review-success">✓</span>
                          Revisión DN
                        </div>
                      )}
                      {project.propuesta_enviada && (
                        <div className="review-status">
                          <span className="review-icon review-info">📤</span>
                          Propuesta Enviada
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Dates */}
                <div className="sidebar-item">
                  <span className="sidebar-label">Fechas Importantes:</span>
                  <div style={{ marginTop: '4px' }}>
                    {project.fecha_de_aprobacion_propuesta_firmada && (
                      <div style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
                        <strong>Aprobación:</strong> {formatDate(project.fecha_de_aprobacion_propuesta_firmada)}
                      </div>
                    )}
                    {project.fecha_de_entrega_propuesta_prevista && (
                      <div style={{ fontSize: '0.85rem' }}>
                        <strong>Entrega Prevista:</strong> {formatDate(project.fecha_de_entrega_propuesta_prevista)}
                      </div>
                    )}
                    {!project.fecha_de_aprobacion_propuesta_firmada && !project.fecha_de_entrega_propuesta_prevista && (
                      <span style={{ color: '#8b949e', fontStyle: 'italic', fontSize: '0.85rem' }}>No hay fechas importantes definidas</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-header">
                <h4 className="sidebar-title">Enlaces y Recursos</h4>
              </div>
              <div className="sidebar-content">
                <div className="resource-links">
                  {enlacesLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: '#666', fontSize: '0.85rem' }}>
                      <span style={{ width: 16, height: 16, border: '2px solid #0078d4', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', flexShrink: 0, animation: 'spin 0.7s linear infinite' }} />
                      Actualizando enlaces...
                    </div>
                  ) : (
                    <>
                      {project.enlace_al_documento_ppt_sharepoint && (
                        <a
                          href={project.enlace_al_documento_ppt_sharepoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link ppt"
                        >
                          <span className="resource-link-icon">📄</span>
                          Documento PPT
                        </a>
                      )}

                      {project.enlace_a_la_pagina_de_confluence && (
                        <a
                          href={project.enlace_a_la_pagina_de_confluence}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link confluence"
                        >
                          <span className="resource-link-icon">📖</span>
                          Confluence
                        </a>
                      )}

                      {project.enlace_propuesta_pdf_sharepoint && (
                        <a
                          href={project.enlace_propuesta_pdf_sharepoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="resource-link pdf"
                        >
                          <span className="resource-link-icon">📋</span>
                          Propuesta PDF
                        </a>
                      )}

                      {!project.enlace_al_documento_ppt_sharepoint &&
                        !project.enlace_a_la_pagina_de_confluence &&
                        !project.enlace_propuesta_pdf_sharepoint && (
                          <div className="empty-state" style={{ padding: '12px', fontSize: '0.85rem' }}>
                            No hay enlaces disponibles{' '}
                            <button
                              onClick={() => setEnlacesModalOpen(true)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#0078d4',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                textDecoration: 'underline',
                                padding: '0',
                                margin: '0'
                              }}
                            >
                              Agregar enlaces
                            </button>
                          </div>
                        )}
                      
                      {/* Enlace sutil para editar si hay enlaces */}
                      {(project.enlace_al_documento_ppt_sharepoint || 
                        project.enlace_a_la_pagina_de_confluence || 
                        project.enlace_propuesta_pdf_sharepoint) && (
                        <button
                          onClick={() => setEnlacesModalOpen(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0078d4',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            textDecoration: 'underline',
                            padding: '8px 0 0 0',
                            margin: '0',
                            display: 'block'
                          }}
                        >
                          ✏️ Editar enlaces
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Sección de Comentarios */}
          <div className="col-12">
            <div className="comments-section">
              <div className="comments-header">
                <h3 className="comments-title">Comentarios</h3>
              </div>
              <div className="comments-content">
                {/* Formulario para agregar comentario */}
                <div className="comment-form">
                  <h5>Agregar comentario</h5>
                  <div style={{ marginBottom: '12px' }}>
                    <MentionTextarea
                      className="comment-textarea"
                      rows={3}
                      value={nuevoComentario}
                      onChange={(val) => setNuevoComentario(val)}
                    />
                  </div>
                  <button
                    className="comment-submit"
                    onClick={agregarComentario}
                    disabled={loadingComentarios || !nuevoComentario.trim()}
                  >
                    {loadingComentarios ? 'Agregando...' : 'Agregar comentario'}
                  </button>
                </div>

                {/* Lista de comentarios */}
                <div className="comments-list">
                  <h5>Comentarios ({comentarios.length})</h5>
                  {comentarios.length === 0 ? (
                    <div className="empty-state">No hay comentarios aún.</div>
                  ) : (
                    <div style={{ marginTop: '16px' }}>
                      {comentarios.map((comentario) => (
                        <div key={comentario.id} className="comment-item" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          {/* Contenido del comentario */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="comment-meta" style={{ marginBottom: '4px' }}>
                              {comentario.autor_nombre && (
                                <span style={{ fontWeight: '600', color: '#2c3e50', marginRight: '6px' }}>
                                  {comentario.autor_nombre}
                                </span>
                              )}
                              Creado: {formatDateTime(comentario.fecha_creacion)}
                              {comentario.fecha_modificacion !== comentario.fecha_creacion && (
                                <span> • Modificado: {formatDateTime(comentario.fecha_modificacion)}</span>
                              )}
                            </div>
                            <div className="comment-content">
                              {editingComentarioId === comentario.id ? (
                                <div>
                                  <textarea
                                    style={{ width: '100%', minHeight: '80px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical', fontSize: '14px' }}
                                    value={editingComentarioTexto}
                                    onChange={(e) => setEditingComentarioTexto(e.target.value)}
                                  />
                                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                    <button
                                      className="comment-submit"
                                      onClick={() => guardarEdicionComentario(comentario.id)}
                                      disabled={!editingComentarioTexto.trim()}
                                    >
                                      Guardar
                                    </button>
                                    <button
                                      style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', background: '#f8f9fa', cursor: 'pointer' }}
                                      onClick={() => { setEditingComentarioId(null); setEditingComentarioTexto(''); }}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                renderMentionText(comentario.contenido)
                              )}
                            </div>
                          </div>
                          {/* Acciones: solo para el autor */}
                          {comentario.autor_nombre === currentUserName && editingComentarioId !== comentario.id && (
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', flexShrink: 0, alignSelf: 'center' }}>
                              <button
                                onClick={() => { setEditingComentarioId(comentario.id); setEditingComentarioTexto(comentario.contenido); }}
                                title="Editar comentario"
                                style={{ background: 'none', border: '1px solid #0366d6', borderRadius: '4px', cursor: 'pointer', color: '#0366d6', fontSize: '13px', padding: '3px 7px', lineHeight: 1, opacity: 0.8 }}
                              >
                                ✏️
                              </button>
                              <button
                                className="comment-delete"
                                onClick={() => eliminarComentario(comentario.id)}
                                title="Eliminar comentario"
                                style={{ fontSize: '13px', padding: '3px 7px' }}
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de supervisión */}
      <SupervisionModal
        open={supervisionModalOpen}
        onClose={handleSupervisionClose}
        onConfirm={handleSupervisionConfirm}
        iniciativaId={id}
        supervisionStatus={supervisionStatus}
        loading={supervisionLoading}
      />
      
      {/* Modal de enlace propuesta */}
      <EnlacePropuestaModal
        open={enlaceModalOpen}
        onClose={handleEnlaceClose}
        onConfirm={handleEnlaceConfirm}
        iniciativaId={id}
        enlaceActual={enlaceActual}
        loading={enlaceLoading}
        estadoDestino={pendingFieldUpdate?.value}
      />
      
      {/* Modal de fecha de entrega real */}
      <FechaEntregaRealModal
        open={fechaEntregaRealModalOpen}
        onClose={handleFechaEntregaRealClose}
        onConfirm={handleFechaEntregaRealConfirm}
        initialDate={new Date().toISOString().split('T')[0]}
      />

      {/* Modal de enlaces */}
      <EnlacesModal
        open={enlacesModalOpen}
        onClose={() => setEnlacesModalOpen(false)}
        enlaces={project}
        onSave={handleSaveEnlaces}
      />

      {/* Modal de motivo de rechazo */}
      <MotivoRechazoModal
        open={motivoRechazoModalOpen}
        onClose={handleMotivoRechazoClose}
        onConfirm={handleMotivoRechazoConfirm}
        motivoActual={project?.motivo_tipificado || ''}
      />

      {/* Modal de campos requeridos para aprobar */}
      <AprobadaModal
        open={aprobadaModalOpen}
        onClose={handleAprobadaClose}
        onConfirm={handleAprobadaConfirm}
        iniciativa={project}
        loading={aprobadaLoading}
      />
      <HitoModal
        open={hitoModalOpen}
        onClose={handleCerrarHitoModal}
        hito={hitoSeleccionado}
        onSave={handleGuardarHitoModal}
        iniciativas={project ? [{ id: project.id, nombre_de_la_oportunidad: project.nombre_de_la_oportunidad, nombre_sociedad_final: project?.negocio_estructura_detail?.nombre_sociedad_final || '', importe_total_cliente: project.importe_total_cliente || null }] : []}
        defaultIniciativaId={project?.id}
      />
    </div>
  );
};

export default ProjectDetail;